import axios from 'axios';
import { handleAuthError } from '../utils/authUtils';

const API_BASE_URL = '/api';

interface LoginResponse {
  success: boolean;
  data: {
    token: string;
    user: {
      id: number;
      username: string;
      email: string;
      role: string;
    };
  };
}

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  role_id?: number;
  role_name?: string;
  sales_agent_id?: number | null;
  permissions?: string[];
}

interface UserNotificationPreference {
  id: number;
  user_id: number;
  notification_type: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  frequency: 'immediate' | 'daily' | 'weekly' | 'never';
  created_at: string;
  updated_at: string;
}

interface UpdateUserNotificationPreferenceData {
  email_enabled?: boolean;
  in_app_enabled?: boolean;
  frequency?: 'immediate' | 'daily' | 'weekly' | 'never';
}

class AuthService {
  async login(email: string, password: string): Promise<{ token: string; user: User }> {
    try {
      const response = await axios.post<LoginResponse>(`${API_BASE_URL}/auth/login`, {
        email,
        password,
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Login failed');
      }
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error('Login failed. Please check your credentials.');
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await axios.get<{ success: boolean; data: User; error?: any }>(
        `${API_BASE_URL}/auth/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        // Include the error message from the response if available
        const errorMessage = response.data.error?.message || 'Failed to get user data';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      // Use centralized auth error handler
      if (handleAuthError(error)) {
        throw new Error('Authentication required');
      }

      // Include more details about the error
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }

      if (error.message) {
        throw error; // Re-throw if it already has a message
      }

      throw new Error('Failed to get user data');
    }
  }

  async logout(): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(
          `${API_BASE_URL}/auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
    }
  }

  // Notification Preferences Management
  async getUserNotificationPreferences(): Promise<UserNotificationPreference[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/profile/notifications`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('Failed to fetch notification preferences');
    } catch (error: any) {
      // Use centralized auth error handler
      if (handleAuthError(error)) {
        throw new Error('Authentication required');
      }
      throw new Error('Failed to fetch notification preferences');
    }
  }

  async updateUserNotificationPreference(
    notificationType: string,
    updates: UpdateUserNotificationPreferenceData
  ): Promise<UserNotificationPreference> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/auth/profile/notifications/${notificationType}`,
        updates
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('Failed to update notification preference');
    } catch (error: any) {
      // Use centralized auth error handler
      if (handleAuthError(error)) {
        throw new Error('Authentication required');
      }
      throw new Error('Failed to update notification preference');
    }
  }
}

export const authService = new AuthService();

export type { User, UserNotificationPreference, UpdateUserNotificationPreferenceData };
